import { useEffect, useState } from 'react'

/**
 * Talking to the Android and iOS store apps, which wrap this web app in a web
 * view (see mobile/). They add `SnapshotMoneyApp` to the user agent and
 * expose `window.ReactNativeWebView.postMessage`; replies come back as
 * `snapshot:reminders` events.
 */

export type ReminderKind = 'weekly' | 'monthly'

export interface ReminderState {
  weekly: boolean
  monthly: boolean
  permission: 'granted' | 'denied' | 'undetermined'
  /** False once the phone will no longer ask, so only its Settings can allow them. */
  canAsk: boolean
}

type NativeWindow = Window & { ReactNativeWebView?: { postMessage: (data: string) => void } }

export function inStoreApp(): boolean {
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('SnapshotMoneyApp')
}

function post(message: object) {
  ;(window as NativeWindow).ReactNativeWebView?.postMessage(JSON.stringify(message))
}

/** Once someone is signed in: the app turns both reminders on the first time. */
export function initReminders() {
  if (inStoreApp()) post({ type: 'reminders:init' })
}

export function setReminder(kind: ReminderKind, on: boolean) {
  post({ type: 'reminders:set', kind, on })
}

export function openPhoneSettings() {
  post({ type: 'reminders:openSettings' })
}

/** The reminders as the phone has them; null until it answers. */
export function useReminders(): ReminderState | null {
  const [state, setState] = useState<ReminderState | null>(null)
  useEffect(() => {
    const onState = (e: Event) => setState((e as CustomEvent<ReminderState>).detail)
    window.addEventListener('snapshot:reminders', onState)
    post({ type: 'reminders:get' })
    return () => window.removeEventListener('snapshot:reminders', onState)
  }, [])
  return state
}
